import type { ReactNode } from 'react';
import {
  Home,
  Plus,
  Users,
  Trophy,
  Calendar,
  Activity,
  Tablet,
  Smartphone,
  Receipt,
  MessageCircle,
} from 'lucide-react';
import { PORTAL_BRAND } from '../../data/brand';
import { APP_VERSION } from '../../data/changelog';
import { useT } from '../../i18n';

// NAV_ITEMS co-extracted verbatim from the monolith (legacy/App.jsx) — the
// nav menu's source of truth for the primary navigation entries. Kept local to
// the only component that reads it.
const NAV_ITEMS = [
  { id: 'home', labelKey: 'ui.nav.item.home', icon: Home },
  {
    id: 'createTournament',
    labelKey: 'ui.nav.item.createTournament',
    icon: Plus,
    group: 'start',
    prominent: true,
  },
  { id: 'bookings', labelKey: 'ui.nav.item.bookings', icon: Calendar, group: 'features' },
  { id: 'activities', labelKey: 'ui.nav.item.activities', icon: Activity, group: 'features' },
  { id: 'banepad', labelKey: 'ui.nav.item.banepad', icon: Tablet, group: 'features' },
  { id: 'mobile', labelKey: 'ui.nav.item.mobile', icon: Smartphone, group: 'features' },
  { id: 'messages', labelKey: 'ui.nav.item.messages', icon: MessageCircle, group: 'features' },
  { id: 'players', labelKey: 'ui.nav.item.players', icon: Users, group: 'data' },
  { id: 'archive', labelKey: 'ui.nav.item.archive', icon: Trophy, group: 'data' },
  { id: 'reporting', labelKey: 'ui.nav.item.reporting', icon: Receipt, group: 'data' },
  // Bug & suggestions — removed from the main menu. Available via the floating
  // button in the bottom-right corner (BugReportButton) that opens
  // BugReportModal with both the new-report form AND an archive tab for
  // viewing existing reports.
  //
  // `as const` so each `labelKey` keeps its literal type and is therefore a
  // MessageKey — a widened `string` would not type-check against `t()`.
] as const;

/**
 * The views the menu opens from Hjem with one click and nothing else (#1145).
 * Each needs no state built up on the way, which is what lets a reload or a
 * bookmark land on it: `initialViewFromLocation` restores exactly these.
 */
export const NAV_VIEW_IDS: readonly string[] = NAV_ITEMS.map((i) => i.id);

export interface NavMenuItemsProps {
  /** Currently active view id (drives the active-item styling). */
  currentView: string;
  /** Navigate to the given view id. */
  onNavigate: (id: string) => void;
  /** Optional callback fired after a nav item is clicked (e.g. close the menu). */
  onItemClick?: () => void;
  /** Optional slot rendered in the menu header (e.g. the pin toggle). */
  headerSlot?: ReactNode;
  /** Badge count shown on the archive item. */
  archiveCount?: number;
  /**
   * The live Padel24Engine wiring object (its `.version` / `.versionName` are
   * read for the engine footer). Passed as a prop by the caller that owns the
   * engine instance.
   */
  Padel24Engine: any;
}

/**
 * The shared list of navigation items rendered inside the hamburger NavMenu and
 * the pinned sidebar. Extracted verbatim from the monolith (legacy/App.jsx);
 * markup unchanged so styling/behaviour are identical.
 */
export function NavMenuItems({
  currentView,
  onNavigate,
  onItemClick,
  headerSlot,
  archiveCount = 0,
  Padel24Engine,
}: NavMenuItemsProps) {
  const t = useT();
  return (
    <>
      {/* Header with pin button (same placement whether pinned or not) */}
      {headerSlot && (
        <div className="flex items-center justify-between px-2 pb-1.5 mb-1 border-b border-line">
          <span className="font-mono text-[9px] uppercase tracking-wider text-ink-3 px-1.5">
            {t('ui.nav.menu')}
          </span>
          {headerSlot}
        </div>
      )}

      {NAV_ITEMS.map((item) => {
        const Icon = item.icon;
        const isActive = currentView === item.id;
        // The archive counter is shown on the "archive" item if there is anything there.
        // Uses muted blue/ink, not red — it is informative, not a warning.
        const isArchiveItem = item.id === 'archive';
        const hasArchiveCount = isArchiveItem && archiveCount > 0;

        return (
          <button
            key={item.id}
            onClick={() => {
              onNavigate(item.id);
              onItemClick?.();
            }}
            className={`w-full text-left px-3 py-2 rounded flex items-center gap-2.5 text-sm transition-colors ${
              isActive ? 'bg-bg-soft font-semibold text-ink' : 'text-ink-2 hover:bg-bg-soft/60'
            }`}
          >
            <span className="flex-shrink-0">
              <Icon size={14} strokeWidth={isActive ? 2.5 : 2} />
            </span>
            <span className="flex-1">{t(item.labelKey)}</span>
            {hasArchiveCount && (
              <span className="font-mono text-[9px] text-ink-3 font-bold">{archiveCount}</span>
            )}
          </button>
        );
      })}

      {/* Version footer: App above Engine, both clickable as menu items.
          The App button routes to ChangelogView, the Engine button to EngineView. */}
      <div className="border-t border-line mt-1 pt-1.5 space-y-0.5">
        <button
          onClick={() => {
            onNavigate('changelog');
            onItemClick?.();
          }}
          className={`w-full text-left px-3 py-1.5 rounded transition-colors ${
            currentView === 'changelog' ? 'bg-bg-soft' : 'hover:bg-bg-soft/60'
          }`}
          title={t('ui.nav.changelogTitle', { brand: PORTAL_BRAND.name })}
        >
          <div className="font-mono text-[9px] text-ink-3 uppercase tracking-wider">
            {t('ui.nav.appLabel', { brand: PORTAL_BRAND.name })}
          </div>
          <div className="font-mono text-[10px] text-ink-2">
            v{APP_VERSION} <span className="text-ink-3">· {t('ui.nav.changelog')}</span>
          </div>
        </button>
        <button
          onClick={() => {
            onNavigate('engine');
            onItemClick?.();
          }}
          className={`w-full text-left px-3 py-1.5 rounded transition-colors ${
            currentView === 'engine' ? 'bg-bg-soft' : 'hover:bg-bg-soft/60'
          }`}
          title={t('ui.nav.engineTitle', { engine: PORTAL_BRAND.engineName })}
        >
          <div className="font-mono text-[9px] text-ink-3 uppercase tracking-wider">
            {PORTAL_BRAND.engineName}
          </div>
          <div className="font-mono text-[10px] text-ink-2">
            v{Padel24Engine.version}{' '}
            <span className="text-ink-3">· {Padel24Engine.versionName}</span>
          </div>
        </button>
      </div>
    </>
  );
}
